import React, { useRef, useEffect, useCallback, useMemo, Suspense, useImperativeHandle, forwardRef } from 'react';
import { LoadPanel } from 'devextreme-react/load-panel';
import Swal from 'sweetalert2';
import { DateUtils } from 'react-day-picker';
import { DuplicateReservationComponent } from '../Shared/DuplicateReservationComponent';
import './reservation-engine.css';

// Sub-components (eagerly loaded)
import CustomerDetailsCard from './AirportTransfer/CustomerDetailsCard';
import AirlineDetailsCard from './AirportTransfer/AirlineDetailsCard';
import DateTimeCard from './AirportTransfer/DateTimeCard';
import PickupDropoffCard from './AirportTransfer/PickupDropoffCard';
import OtherDetailsCard from './AirportTransfer/OtherDetailsCard';

// Hooks
import { useAirportState } from './AirportTransfer/hooks/useAirportState';
import { useAirportApi } from './AirportTransfer/hooks/useAirportApi';
import { useAirportValidation } from './AirportTransfer/hooks/useAirportValidation';

// Lazy-loaded fare summary (only shown when sessionAccountId === sessionMappedAccountId3 && rideId > 0)
const FareSummaryCard = React.lazy(() => import('./AirportTransfer/FareSummaryCard'));

const position = { of: '#bookingdiv' };
const google = window.google;
const sessionAccountId = parseInt(sessionStorage.getItem('session_accountId') || '0', 10);
const sessionMappedAccountId3 = parseInt(sessionStorage.getItem('session_mappedAccountId3') || '0', 10);

export const AirportTransferComponent = forwardRef(function AirportTransferComponent(props, ref) {
  const { customer, tripDetails } = props;

  // Refs
  const vehicleDriverRef = useRef(null);
  const duplicationModalRef = useRef(null);
  const rideTypeIdRef = useRef(0);
  const isLoadingTimeRef = useRef(false);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);

  // State
  const { state, setState } = useAirportState(customer, tripDetails);

  // API
  const api = useAirportApi(state, setState, rideTypeIdRef, isLoadingTimeRef, vehicleDriverRef, duplicationModalRef, props);

  // Validation
  const { validate } = useAirportValidation(state, setState);

  // Mount: load page data + init Google Places services
  useEffect(() => {
    let isMounted = true;
    setState({ loadPanelVisible: true });

    const init = async () => {
      if (state.customer != null) {
        await api.loadPage(state.customer);
      }
      // Init AutocompleteService for programmatic predictions (no pac-container)
      if (google && google.maps && google.maps.places) {
        autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
        const div = document.createElement('div');
        placesServiceRef.current = new google.maps.places.PlacesService(div);
      }
      if (isMounted) setState({ loadPanelVisible: false });
    };

    init();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Input handlers ----

  const handleInputChange = useCallback((event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    if (name === 'recurringTypeId') {
      setState({
        selectedDays: [],
        isWeekSun: false, isWeekMon: false, isWeekTue: false, isWeekWed: false, isWeekThu: false, isWeekFri: false, isWeekSat: false,
        isMonthSun: false, isMonthMon: false, isMonthTue: false, isMonthWed: false, isMonthThu: false, isMonthFri: false, isMonthSat: false,
        [name]: value,
      });
      setState((prev) => ({ errors: { ...prev.errors, recurring: '' } }));
    } else if (name === 'serviceSourceId') {
      if (value === '1') setState({ otherAffiliateId: 0 });
      setState({ [name]: value });
      setState((prev) => ({ errors: { ...prev.errors, [name]: '' } }));
    } else {
      setState({ [name]: value });
      setState((prev) => ({ errors: { ...prev.errors, [name]: '' } }));
    }
  }, [setState]);

  const handlePickupZoneChange = useCallback((selectedOption) => {
    const zoneId = selectedOption.selectedItem != null ? selectedOption.selectedItem.id : 0;
    setState({ pickupZoneId: zoneId });
    setState((prev) => ({ errors: { ...prev.errors, pickupZoneId: '' } }));
    if (selectedOption.selectedItem != null) {
      api.getRideTypeId(zoneId, state.dropoffZoneId);
      const paxCount = parseInt(state.pilotCount | 0) + parseInt(state.crewCount | 0) + parseInt(state.flightAttandCount | 0);
      api.getCustomerRateTaxSetting(zoneId, state.dropoffZoneId, state.puDateTime, state.vehicleTypeId, paxCount, 0, state.isMeetGreet);
    }
  }, [state, setState, api]);

  const handleDropoffZoneChange = useCallback((selectedOption) => {
    const zoneId = selectedOption.selectedItem != null ? selectedOption.selectedItem.id : 0;
    setState({ dropoffZoneId: zoneId });
    setState((prev) => ({ errors: { ...prev.errors, dropoffZoneId: '' } }));
    if (selectedOption.selectedItem != null) {
      api.getRideTypeId(state.pickupZoneId, zoneId);
      const paxCount = parseInt(state.pilotCount | 0) + parseInt(state.crewCount | 0) + parseInt(state.flightAttandCount | 0);
      api.getCustomerRateTaxSetting(state.pickupZoneId, zoneId, state.puDateTime, state.vehicleTypeId, paxCount, 0, state.isMeetGreet);
    }
  }, [state, setState, api]);

  const handleAirlineChange = useCallback((selectedOption) => {
    setState({ airlineId: selectedOption.selectedItem != null ? selectedOption.selectedItem.airLineId : 0 });
    setState((prev) => ({ errors: { ...prev.errors, airlineId: '' } }));
  }, [setState]);

  const handleAirlineReturnChange = useCallback((selectedOption) => {
    setState({ airlineIdReturn: selectedOption.selectedItem != null ? selectedOption.selectedItem.airLineId : 0 });
    setState((prev) => ({ errors: { ...prev.errors, airlineIdReturn: '' } }));
  }, [setState]);

  const handleVehicleTypeChange = useCallback((selectedOption) => {
    const vTypeId = Number(selectedOption.selectedItem != null ? selectedOption.selectedItem.rowIndex : 0);
    setState({ vehicleTypeId: vTypeId });
    setState((prev) => ({ errors: { ...prev.errors, vehicleTypeId: '' } }));
    if (selectedOption != null) {
      const paxCount = parseInt(state.pilotCount | 0) + parseInt(state.crewCount | 0) + parseInt(state.flightAttandCount | 0);
      api.getCustomerRateTaxSetting(state.pickupZoneId, state.dropoffZoneId, state.puDateTime, vTypeId, paxCount, 0, state.isMeetGreet);
    }
  }, [state, setState, api]);

  const handleAffiliateChange = useCallback((selectedOption) => {
    setState({ otherAffiliateId: selectedOption.selectedItem != null ? selectedOption.selectedItem.id : 0 });
    setState((prev) => ({ errors: { ...prev.errors, otherAffiliateId: '' } }));
  }, [setState]);

  const handleDayClick = useCallback((day, { selected }) => {
    const selectedDays = [...state.selectedDays];
    if (selected) {
      const selectedIndex = selectedDays.findIndex((d) => DateUtils.isSameDay(d, day));
      selectedDays.splice(selectedIndex, 1);
    } else {
      selectedDays.push(day);
    }
    setState({ selectedDays });
    setState((prev) => ({ errors: { ...prev.errors, recurring: '' } }));
  }, [state.selectedDays, setState]);

  const handlePaxCountChange = useCallback((pType, value) => {
    let paxCount = 0;
    if (pType === 'p') {
      setState({ pilotCount: value });
      paxCount = parseInt(value | 0) + parseInt(state.crewCount | 0) + parseInt(state.flightAttandCount | 0);
    } else if (pType === 'c') {
      setState({ crewCount: value });
      paxCount = parseInt(state.pilotCount | 0) + parseInt(value | 0) + parseInt(state.flightAttandCount | 0);
    } else if (pType === 'f') {
      setState({ flightAttandCount: value });
      paxCount = parseInt(state.pilotCount | 0) + parseInt(state.crewCount | 0) + parseInt(value | 0);
    }
    api.getCustomerRateTaxSetting(state.pickupZoneId, state.dropoffZoneId, state.puDateTime, state.vehicleTypeId, paxCount, 0, state.isMeetGreet);
    if (paxCount > 0) setState((prev) => ({ errors: { ...prev.errors, pilotCount: '' } }));
  }, [state, setState, api]);

  const handleTripCountChange = useCallback((value) => {
    setState({ noOfTrip: value });
    setState((prev) => ({ errors: { ...prev.errors, noOfTrip: '' } }));
  }, [setState]);

  const handlePaxCountReturnChange = useCallback((pType, value) => {
    if (pType === 'p') setState({ pilotCountReturn: value });
    else if (pType === 'c') setState({ crewCountReturn: value });
    else if (pType === 'f') setState({ flightAttandCountReturn: value });
  }, [setState]);

  const handleTripTypeChange = useCallback((event) => {
    const value = event.target.value;
    const currentTime = sessionStorage.getItem('session_locationTime') === '' ? new Date() : new Date(sessionStorage.getItem('session_locationTime'));
    const currentHour = new Date(currentTime).getHours();
    const currentMinute = new Date(currentTime).getMinutes();
    setState({
      tripTypeId: value,
      recurringTypeId: 2, selectedDays: [], recurringEndDate: '',
      isWeekSun: false, isWeekMon: false, isWeekTue: false, isWeekWed: false, isWeekThu: false, isWeekFri: false, isWeekSat: false,
      isMonthSun: false, isMonthMon: false, isMonthTue: false, isMonthWed: false, isMonthThu: false, isMonthFri: false, isMonthSat: false,
      flightNumberReturn: '', terminalTypeReturn: 1, terminalReturn: '', noOfTrip: 1,
      flightHourReturn: currentHour, flightMinuteReturn: currentMinute,
      flightTimeReturn: currentTime, pilotCountReturn: 0, crewCountReturn: 0, flightAttandCountReturn: 0,
    });
    setState((prev) => ({
      errors: {
        ...prev.errors,
        recurring: '', airlineIdReturn: '', flightNumberReturn: '', pilotCountReturn: '', flightTimeReturn: '', puDateTimeReturn: '',
      },
    }));
  }, [setState]);

  const handleMeetGreetChange = useCallback((event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setState({ isMeetGreet: value });
    const paxCount = parseInt(state.pilotCount | 0) + parseInt(state.crewCount | 0) + parseInt(state.flightAttandCount | 0);
    api.getCustomerRateTaxSetting(state.pickupZoneId, state.dropoffZoneId, state.puDateTime, state.vehicleTypeId, paxCount, 0, value);
  }, [state, setState, api]);

  const handleRecurringEndDateChange = useCallback((date) => {
    setState({ recurringEndDate: date.value });
    setState((prev) => ({ errors: { ...prev.errors, recurringEndDate: '' } }));
  }, [setState]);

  const handleFlightHourChange = useCallback((e) => {
    if (e.selectedItem != null) setState({ flightHour: e.selectedItem.id });
  }, [setState]);

  const handleFlightMinuteChange = useCallback((e) => {
    if (e.selectedItem != null) setState({ flightMinute: e.selectedItem.id });
  }, [setState]);

  const handleFlightHourReturnChange = useCallback((e) => {
    if (e.selectedItem != null) setState({ flightHourReturn: e.selectedItem.id });
  }, [setState]);

  const handleFlightMinuteReturnChange = useCallback((e) => {
    if (e.selectedItem != null) setState({ flightMinuteReturn: e.selectedItem.id });
  }, [setState]);

  const handlePickupDateTimeChange = useCallback((date) => {
    setState({ puDateTime: date.value });
    setState((prev) => ({ errors: { ...prev.errors, puDateTime: '' } }));
    if (date != null) {
      const paxCount = parseInt(state.pilotCount | 0) + parseInt(state.crewCount | 0) + parseInt(state.flightAttandCount | 0);
      api.getCustomerRateTaxSetting(state.pickupZoneId, state.dropoffZoneId, date.value, state.vehicleTypeId, paxCount, 0, state.isMeetGreet);
    }
  }, [state, setState, api]);

  const handlePickupDateTimeReturnChange = useCallback((date) => {
    setState({ puDateTimeReturn: date.value });
    setState((prev) => ({ errors: { ...prev.errors, puDateTimeReturn: '' } }));
  }, [setState]);

  // ---- Autosuggest pickup handlers ----
  const onChangePickupLocation = useCallback((event, { newValue }) => {
    setState({ pickupLocation: newValue });
    setState((prev) => ({ errors: { ...prev.errors, pickupLocation: '' } }));
  }, [setState]);

  const onBlurPickupLocation = useCallback(() => {
    if (state.isPickupGoogleBooking) return; // Google mode: keep whatever user typed
    const index2 = state.bookingAddress.length > 0 ? state.bookingAddress.map((item) => item.name).indexOf(state.pickupLocation) : -1;
    if (index2 === -1) {
      setState({ pickupLocation: '', pickupAddress: '', pickupAddressType: '', pickupCity: '', pickupState: '', pickupZipCode: '', pickupZoneId: 0, pickupLatitude: 0, pickupLongitude: 0 });
      api.getRideTypeId(0, state.dropoffZoneId);
    }
    setState((prev) => ({ errors: { ...prev.errors, pickupLocation: '' } }));
  }, [state, setState, api]);

  // ---- Google place detail parser (accepts PlaceResult directly) ----
  const parseGooglePlaceResult = useCallback((place) => {
    const placeObject = {};
    if (place == null || !place.formatted_address || place.formatted_address.trim() === '') return placeObject;
    placeObject.location = place.name || '';
    placeObject.latitude = place.geometry?.location?.lat() || 0;
    placeObject.longitude = place.geometry?.location?.lng() || 0;
    let splitAddress = place.formatted_address.replace(/-/g, ',').split(',');
    if (splitAddress.length === 1) { placeObject.address1 = splitAddress[0]; placeObject.cityName = ''; placeObject.zipCode = ''; placeObject.stateName = ''; placeObject.countryName = ''; }
    else if (splitAddress.length === 2) { placeObject.address1 = splitAddress[0]; placeObject.cityName = splitAddress[0]; placeObject.stateName = splitAddress[1].trim().search(' ') > 0 ? splitAddress[1].trim().split(' ')[0] : splitAddress[1].trim(); placeObject.zipCode = splitAddress[1].trim().search(' ') > 0 ? splitAddress[1].trim().split(' ')[1] : ' '; placeObject.countryName = ''; }
    else if (splitAddress.length === 3) { placeObject.address1 = splitAddress[0] + ' ' + splitAddress[1].trim(); placeObject.cityName = splitAddress[1].trim(); placeObject.stateName = splitAddress[2].trim().search(' ') > 0 ? splitAddress[2].trim().split(' ')[0] : splitAddress[2].trim(); placeObject.zipCode = splitAddress[2].trim().search(' ') > 0 ? splitAddress[2].trim().split(' ')[1] : ' '; placeObject.countryName = ''; }
    else if (splitAddress.length === 4) { placeObject.address1 = splitAddress[0] + ' ' + splitAddress[1].trim(); placeObject.cityName = splitAddress[1].trim(); placeObject.stateName = splitAddress[2].trim().search(' ') > 0 ? splitAddress[2].trim().split(' ')[0] : splitAddress[2].trim(); placeObject.zipCode = splitAddress[2].trim().search(' ') > 0 ? splitAddress[2].trim().split(' ')[1] : ' '; placeObject.countryName = splitAddress[3].trim(); }
    else if (splitAddress.length === 5) { placeObject.address1 = splitAddress[0] + ' ' + splitAddress[1].trim() + ' ' + splitAddress[2].trim(); placeObject.cityName = splitAddress[2].trim(); placeObject.stateName = splitAddress[3].trim().search(' ') > 0 ? splitAddress[3].trim().split(' ')[0] : splitAddress[3].trim(); placeObject.zipCode = splitAddress[3].trim().search(' ') > 0 ? splitAddress[3].trim().split(' ')[1] : ' '; placeObject.countryName = splitAddress[4].trim(); }
    else if (splitAddress.length === 6) { placeObject.address1 = splitAddress[0] + ' ' + splitAddress[1].trim() + ' ' + splitAddress[2].trim() + ' ' + splitAddress[3].trim(); placeObject.cityName = splitAddress[3].trim(); placeObject.stateName = splitAddress[4].trim().search(' ') > 0 ? splitAddress[4].trim().split(' ')[0] : splitAddress[4].trim(); placeObject.zipCode = splitAddress[4].trim().search(' ') > 0 ? splitAddress[4].trim().split(' ')[1] : ' '; placeObject.countryName = splitAddress[5].trim(); }
    else { placeObject.address1 = splitAddress[0] + ' ' + splitAddress[1].trim() + ' ' + splitAddress[2].trim() + ' ' + splitAddress[3].trim() + ' ' + splitAddress[4].trim(); placeObject.cityName = splitAddress[4].trim(); placeObject.stateName = splitAddress[5].trim().search(' ') > 0 ? splitAddress[5].trim().split(' ')[0] : splitAddress[5].trim(); placeObject.zipCode = splitAddress[5].trim().search(' ') > 0 ? splitAddress[5].trim().split(' ')[1] : ' '; placeObject.countryName = splitAddress[6] ? splitAddress[6].trim() : ''; }
    return placeObject;
  }, []);

  // getSuggestionValue must be pure (no side effects) — called on every arrow key press
  const getPickupSuggestionValue = useCallback((suggestion) => {
    if (suggestion.isNotFound) return '';
    return suggestion.name || '';
  }, []);

  // onSuggestionSelected fires only on actual selection (click / Enter)
  const onPickupSuggestionSelected = useCallback((event, { suggestion }) => {
    if (suggestion.isNotFound) return;
    if (suggestion.isGooglePlace) {
      setState((prev) => ({ errors: { ...prev.errors, pickupLocation: '' } }));
      if (placesServiceRef.current && suggestion.placeId) {
        placesServiceRef.current.getDetails(
          { placeId: suggestion.placeId, fields: ['name', 'formatted_address', 'geometry'] },
          (place, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK) {
              const placeObj = parseGooglePlaceResult(place);
              // Keep pickupLocation as the full suggestion.name (already set by onChange)
              // Only update address detail fields from getDetails
              setState({ pickupAddress: placeObj.address1 || '', pickupAddressType: '', pickupCity: placeObj.cityName || '', pickupState: placeObj.stateName || '', pickupZipCode: placeObj.zipCode || '', pickupLatitude: placeObj.latitude || 0, pickupLongitude: placeObj.longitude || 0 });
            }
          }
        );
      }
    } else {
      setState({
        pickupLocation: suggestion.name, pickupAddress: suggestion.address, pickupAddressType: suggestion.addressType,
        pickupCity: suggestion.city, pickupState: suggestion.state, pickupZipCode: suggestion.zipCode,
        pickupZoneId: suggestion.zoneId, pickupLatitude: suggestion.latitude, pickupLongitude: suggestion.longitude,
      });
      if (state.dropdownZone.findIndex((opt) => opt.id === suggestion.zoneId) < 0) {
        setState({ pickupZoneId: 0 });
      } else {
        api.getRideTypeId(suggestion.zoneId, state.dropoffZoneId);
        const paxCount = parseInt(state.pilotCount | 0) + parseInt(state.crewCount | 0) + parseInt(state.flightAttandCount | 0);
        api.getCustomerRateTaxSetting(suggestion.id, state.dropoffZoneId, state.puDateTime, state.vehicleTypeId, paxCount, 0, state.isMeetGreet);
        setState((prev) => ({ errors: { ...prev.errors, pickupZoneId: '' } }));
      }
      setState((prev) => ({ errors: { ...prev.errors, pickupLocation: '' } }));
    }
  }, [state, setState, api, parseGooglePlaceResult]);

  const onPickupSuggestionsFetchRequested = useCallback(({ value }) => {
    if (state.isPickupGoogleBooking && autocompleteServiceRef.current) {
      autocompleteServiceRef.current.getPlacePredictions(
        { input: value, types: ['establishment'] },
        (predictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            setState({ pickupSuggestions: predictions.map(p => ({ name: p.description, placeId: p.place_id, isGooglePlace: true })) });
          } else {
            setState({ pickupSuggestions: [{ isNotFound: true }] });
          }
        }
      );
    } else {
      api.getBookingSuggestions(value).then((data) => setState({ pickupSuggestions: data }));
    }
  }, [state.isPickupGoogleBooking, api, setState]);

  const onPickupSuggestionsClearRequested = useCallback(() => {
    setState({ pickupSuggestions: [] });
    setState((prev) => ({ errors: { ...prev.errors, pickupLocation: '' } }));
  }, [setState]);

  // ---- Autosuggest dropoff handlers ----
  const onChangeDropoffLocation = useCallback((event, { newValue }) => {
    setState({ dropoffLocation: newValue });
    setState((prev) => ({ errors: { ...prev.errors, dropoffLocation: '' } }));
  }, [setState]);

  const onBlurDropoffLocation = useCallback(() => {
    if (state.isDropoffGoogleBooking) return; // Google mode: keep whatever user typed
    const index2 = state.bookingAddress.length > 0 ? state.bookingAddress.map((item) => item.name).indexOf(state.dropoffLocation) : -1;
    if (index2 === -1) {
      setState({ dropoffLocation: '', dropoffAddress: '', dropoffAddressType: '', dropoffCity: '', dropoffState: '', dropoffZipCode: '', dropoffZoneId: 0, dropoffLatitude: 0, dropoffLongitude: 0 });
      api.getRideTypeId(state.pickupZoneId, 0);
    }
    setState((prev) => ({ errors: { ...prev.errors, dropoffLocation: '' } }));
  }, [state, setState, api]);

  const getDropoffSuggestionValue = useCallback((suggestion) => {
    if (suggestion.isNotFound) return '';
    return suggestion.name || '';
  }, []);

  const onDropoffSuggestionSelected = useCallback((event, { suggestion }) => {
    if (suggestion.isNotFound) return;
    if (suggestion.isGooglePlace) {
      setState((prev) => ({ errors: { ...prev.errors, dropoffLocation: '' } }));
      if (placesServiceRef.current && suggestion.placeId) {
        placesServiceRef.current.getDetails(
          { placeId: suggestion.placeId, fields: ['name', 'formatted_address', 'geometry'] },
          (place, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK) {
              const placeObj = parseGooglePlaceResult(place);
              setState({ dropoffAddress: placeObj.address1 || '', dropoffAddressType: '', dropoffCity: placeObj.cityName || '', dropoffState: placeObj.stateName || '', dropoffZipCode: placeObj.zipCode || '', dropoffLatitude: placeObj.latitude || 0, dropoffLongitude: placeObj.longitude || 0 });
            }
          }
        );
      }
    } else {
      setState({
        dropoffLocation: suggestion.name, dropoffAddress: suggestion.address, dropoffAddressType: suggestion.addressType,
        dropoffCity: suggestion.city, dropoffState: suggestion.state, dropoffZipCode: suggestion.zipCode,
        dropoffZoneId: suggestion.zoneId, dropoffLatitude: suggestion.latitude, dropoffLongitude: suggestion.longitude,
      });
      if (state.dropdownZone.findIndex((opt) => opt.id === suggestion.zoneId) < 0) {
        setState({ dropoffZoneId: 0 });
      } else {
        api.getRideTypeId(state.pickupZoneId, suggestion.zoneId);
        const paxCount = parseInt(state.pilotCount | 0) + parseInt(state.crewCount | 0) + parseInt(state.flightAttandCount | 0);
        api.getCustomerRateTaxSetting(state.pickupZoneId, suggestion.zoneId, state.puDateTime, state.vehicleTypeId, paxCount, 0, state.isMeetGreet);
        setState((prev) => ({ errors: { ...prev.errors, dropoffZoneId: '' } }));
      }
      setState((prev) => ({ errors: { ...prev.errors, dropoffLocation: '' } }));
    }
  }, [state, setState, api, parseGooglePlaceResult]);

  const onDropoffSuggestionsFetchRequested = useCallback(({ value }) => {
    if (state.isDropoffGoogleBooking && autocompleteServiceRef.current) {
      autocompleteServiceRef.current.getPlacePredictions(
        { input: value, types: ['establishment'] },
        (predictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            setState({ dropoffSuggestions: predictions.map(p => ({ name: p.description, placeId: p.place_id, isGooglePlace: true })) });
          } else {
            setState({ dropoffSuggestions: [{ isNotFound: true }] });
          }
        }
      );
    } else {
      api.getBookingSuggestions(value).then((data) => setState({ dropoffSuggestions: data }));
    }
  }, [state.isDropoffGoogleBooking, api, setState]);

  const onDropoffSuggestionsClearRequested = useCallback(() => {
    setState({ dropoffSuggestions: [] });
    setState((prev) => ({ errors: { ...prev.errors, dropoffLocation: '' } }));
  }, [setState]);

  // ---- Toggle handlers ----
  const handlePickupToggleChange = useCallback((event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setState({ pickupLocation: '', pickupAddress: '', pickupAddressType: '', pickupCity: '', pickupState: '', pickupZipCode: '', pickupLatitude: 0, pickupLongitude: 0, pickupSuggestions: [], isPickupGoogleBooking: value });
    setState((prev) => ({ errors: { ...prev.errors, pickupLocation: '' } }));
  }, [setState]);

  const handleDropoffToggleChange = useCallback((event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setState({ dropoffLocation: '', dropoffAddress: '', dropoffAddressType: '', dropoffCity: '', dropoffState: '', dropoffZipCode: '', dropoffLatitude: 0, dropoffLongitude: 0, dropoffSuggestions: [], isDropoffGoogleBooking: value });
    setState((prev) => ({ errors: { ...prev.errors, dropoffLocation: '' } }));
  }, [setState]);

  // ---- Submit ----
  const airportTransferSubmit = useCallback(async (isAttachment, isSendEmail) => {
    setTimeout(() => setState({ isAttachment, isSendEmail }), 100);

    if (!validate()) return;

    if (state.rideId > 0) {
      if (parseInt(props.tripDetails.tripStatusId) === 116) {
        if (parseInt(props.tripDetails.invoiceNo) > 0) {
          await Swal.fire({ title: '<small>Unable to modify</small>', text: "Reservation is completed & invoiced, you can't modify/update Reservation.", icon: 'info', dangerMode: false, width: '25%', allowOutsideClick: false });
          return;
        } else {
          const uTypeId = parseInt(window.sessionStorage.getItem('session_userTypeId'));
          if (uTypeId !== 33 && uTypeId !== 6 && uTypeId !== 2) {
            await Swal.fire({ title: '<small>Unable to modify</small>', text: "Reservation is completed, you can't modify/update Reservation.", icon: 'info', dangerMode: false, width: '25%', allowOutsideClick: false });
            return;
          }
        }
      }
      if (parseInt(props.tripDetails.tripStatusId) === 111 || parseInt(props.tripDetails.tripStatusId) === 118) {
        await Swal.fire({ title: '<small>Unable to modify</small>', text: "Reservation is void/cancelled, you can't modify/update Reservation.", icon: 'info', dangerMode: false, width: '25%', allowOutsideClick: false });
        return;
      }
    }

    if (parseInt(state.customer.accountStatusId) === 2) {
      await Swal.fire({ title: '<small>Account on hold</small>', text: 'This account is on hold. You are not allow to book any reservation. <br>Please contact to accounting department.', icon: 'info', dangerMode: false, width: '25%', allowOutsideClick: false });
      return;
    }
    if (parseInt(state.customer.accountStatusId) === 3) {
      await Swal.fire({ title: '<small>Account on close</small>', text: 'This account is on close. You are not allow to book any reservation. <br>Please contact to accounting department.', icon: 'info', dangerMode: false, width: '25%', allowOutsideClick: false });
      return;
    }

    // Schedule validation
    if (state.rideId > 0) {
      if (parseInt(props.tripDetails.tripStatusId) === 101 || parseInt(props.tripDetails.tripStatusId) === 103 || parseInt(props.tripDetails.tripStatusId) === 105) {
        if (rideTypeIdRef.current === 2 && state.airlineId > 0) {
          const diffInMs = Math.abs(state.puDateTime - new Date(state.puDateTime.toLocaleDateString() + ' ' + state.flightTime.toLocaleTimeString()));
          const minuteDiff = diffInMs / (1000 * 60);
          if (minuteDiff <= 0 || minuteDiff < 10) {
            const isConfirm = await api.askScheduleTimeConfirm();
            if (!isConfirm) return;
          }
        }
      }
    } else {
      if (rideTypeIdRef.current === 2 && state.airlineId > 0) {
        const diffInMs = Math.abs(state.puDateTime - new Date(state.puDateTime.toLocaleDateString() + ' ' + state.flightTime.toLocaleTimeString()));
        const minuteDiff = diffInMs / (1000 * 60);
        if (minuteDiff <= 0 || minuteDiff < 10) {
          const isConfirm = await api.askScheduleTimeConfirm();
          if (!isConfirm) return;
        }
      }
    }

    // Pax count validation
    const paxCount = parseInt(state.pilotCount | 0) + parseInt(state.crewCount | 0) + parseInt(state.flightAttandCount | 0);
    const vehicleDetails = state.dropdownVehicleType.filter((opt) => opt.rowIndex === state.vehicleTypeId);
    const apiPaxNumber = vehicleDetails[0]?.apiPaxNumber ?? 0;
    if (apiPaxNumber > 0 && paxCount > apiPaxNumber) {
      const apiVehicleTypeDes = vehicleDetails.length > 0 ? `for ${vehicleDetails[0].apiVehicleTypeDescription}` : '';
      const msgText = `Please enter pax count less than or equal to ${apiPaxNumber} ${apiVehicleTypeDes}!`;
      await Swal.fire({ title: '<small>Invalid Pax Count</small>', text: msgText, icon: 'info', dangerMode: false, width: '25%', allowOutsideClick: false });
      return;
    }

    // Customer rate tax — use returned value to avoid stale-closure false-null on state
    let customerRateTax = state.customerRateTax;
    if (customerRateTax == null) {
      customerRateTax = await api.getCustomerRateTaxSetting(state.pickupZoneId, state.dropoffZoneId, state.puDateTime, state.vehicleTypeId, paxCount, 0, state.isMeetGreet);
    }
    if (customerRateTax == null) {
      await Swal.fire({ title: '<small>Oops..</small>', text: 'Customer setting not found!', icon: 'warning', dangerMode: false, allowOutsideClick: false });
      return;
    }

    if (state.tripTypeId === '2') {
      const isConfirm = await api.askReturnTripConfirm();
      if (!isConfirm) return;
    }

    const isBookingConfirm = await api.askBookingConfirm(paxCount);
    if (!isBookingConfirm) return;

    try {
      if (state.rideId > 0) {
        props.submitFunction(true);
        await api.bookReservation();
      } else {
        props.submitFunction(true);
        const isDuplicateFound = await api.checkDuplicateReservation(true);
        if (isDuplicateFound) {
          props.submitFunction(false);
        } else {
          await api.bookReservation();
        }
      }
    } catch (e) {
      props.submitFunction(false);
    }
  }, [state, setState, validate, api, props, rideTypeIdRef]);

  // Expose methods to parent class component via ref (BookingComponent uses this.refs.airportTransferRef.method())
  useImperativeHandle(ref, () => ({
    airportTransferSubmit,
    copyReservation: api.copyReservation,
    loadPage: api.loadPage,
    checkDuplicateReservation: api.checkDuplicateReservation,
    clearAllControls: () => {},
  }), [airportTransferSubmit, api.copyReservation, api.loadPage, api.checkDuplicateReservation]);

  useEffect(() => {
    if (props.onReady) props.onReady({ airportTransferSubmit, copyReservation: api.copyReservation });
  }, [airportTransferSubmit, api.copyReservation, props]);

  // Notes tab handler
  const handleNotesTabChange = useCallback((tab) => {
    setState({ notesTab: tab });
  }, [setState]);

  // inputProps for autosuggest (memoised)
  const inputPropsPickup = useMemo(() => ({
    placeholder: state.isPickupGoogleBooking ? 'Type to search Google address...' : 'Enter pick-up location.',
    value: state.pickupLocation,
    onChange: onChangePickupLocation,
    onBlur: onBlurPickupLocation,
  }), [state.pickupLocation, state.isPickupGoogleBooking, onChangePickupLocation, onBlurPickupLocation]);

  const inputPropsDropoff = useMemo(() => ({
    placeholder: state.isDropoffGoogleBooking ? 'Type to search Google address...' : 'Enter drop off location.',
    value: state.dropoffLocation,
    onChange: onChangeDropoffLocation,
    onBlur: onBlurDropoffLocation,
  }), [state.dropoffLocation, state.isDropoffGoogleBooking, onChangeDropoffLocation, onBlurDropoffLocation]);

  const customerType = customer != null ? customer.customerType : '';

  return (
    <div className="rx-rez-area" id="bookingdiv">
      {/* Row 1: always 3-col — Date & Time card is always visible */}
      <div className="rx-row-3col">
        <CustomerDetailsCard
          state={state}
          onChange={handleInputChange}
          errors={state.errors}
          customerType={customerType}
        />
        <AirlineDetailsCard
          state={state}
          errors={state.errors}
          onChange={handleInputChange}
          onAirlineChange={handleAirlineChange}
          onAirlineReturnChange={handleAirlineReturnChange}
          onFlightHourChange={handleFlightHourChange}
          onFlightMinuteChange={handleFlightMinuteChange}
          onFlightHourReturnChange={handleFlightHourReturnChange}
          onFlightMinuteReturnChange={handleFlightMinuteReturnChange}
          onPaxCountChange={handlePaxCountChange}
          onPaxCountReturnChange={handlePaxCountReturnChange}
          onTripTypeChange={handleTripTypeChange}
          onMeetGreetChange={handleMeetGreetChange}
          rideTypeId={rideTypeIdRef.current}
        />
        <DateTimeCard
          state={state}
          errors={state.errors}
          onVehicleTypeChange={handleVehicleTypeChange}
          onTripCountChange={handleTripCountChange}
          onPickupDateTimeChange={handlePickupDateTimeChange}
          onPickupDateTimeReturnChange={handlePickupDateTimeReturnChange}
          onRecurringEndDateChange={handleRecurringEndDateChange}
          onChange={handleInputChange}
          onDayClick={handleDayClick}
          onPaxCountReturnChange={handlePaxCountReturnChange}
          onFlightHourReturnChange={handleFlightHourReturnChange}
          onFlightMinuteReturnChange={handleFlightMinuteReturnChange}
        />
      </div>

      {/* Row 2: Pickup/Dropoff + Other Details */}
      <div className="rx-row-2col">
        <PickupDropoffCard
          state={state}
          errors={state.errors}
          inputPropsPickup={inputPropsPickup}
          inputPropsDropoff={inputPropsDropoff}
          onPickupSuggestionsFetchRequested={onPickupSuggestionsFetchRequested}
          onPickupSuggestionsClearRequested={onPickupSuggestionsClearRequested}
          getPickupSuggestionValue={getPickupSuggestionValue}
          onDropoffSuggestionsFetchRequested={onDropoffSuggestionsFetchRequested}
          onDropoffSuggestionsClearRequested={onDropoffSuggestionsClearRequested}
          getDropoffSuggestionValue={getDropoffSuggestionValue}
          onPickupSuggestionSelected={onPickupSuggestionSelected}
          onDropoffSuggestionSelected={onDropoffSuggestionSelected}
          onPickupToggleChange={handlePickupToggleChange}
          onDropoffToggleChange={handleDropoffToggleChange}
          onPickupZoneChange={handlePickupZoneChange}
          onDropoffZoneChange={handleDropoffZoneChange}
        />
        <OtherDetailsCard
          state={state}
          errors={state.errors}
          onChange={handleInputChange}
          onAffiliateChange={handleAffiliateChange}
          vehicleDriverRef={vehicleDriverRef}
          onNotesTabChange={handleNotesTabChange}
        />
      </div>

      {/* Fare Summary (lazy-loaded, conditional) */}
      {sessionAccountId === sessionMappedAccountId3 && state.rideId > 0 && (
        <Suspense fallback={null}>
          <FareSummaryCard state={state} onChange={handleInputChange} />
        </Suspense>
      )}

      <DuplicateReservationComponent ref={duplicationModalRef} bookReservation={api.bookReservation} />
      <LoadPanel
        shadingColor="rgba(0,0,0,0.4)"
        position={position}
        visible={state.loadPanelVisible}
        showIndicator={true}
        shading={true}
        showPane={true}
        closeOnOutsideClick={false}
      />
    </div>
  );
});

export default AirportTransferComponent;
